import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import OrderIndicator from "components/OrderIndicator";
import { updateProfile, reset, profile as getProfile } from "redux/subreseller/profile/profileSlice";
import { BiLoaderCircle } from 'react-icons/bi';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { toast } from "react-toastify";


const ProfileContainer = () => {
  const dispatch = useDispatch();

  const { profile, message, isEditSuccess, isEditError, isEditLoading } = useSelector((state) => state.subresellerProfile);

  // Password change states
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
    otp: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [passwordChangeStep, setPasswordChangeStep] = useState(1); // 1: password input, 2: OTP verification

  useEffect(() => {
    if (isEditSuccess) {
      if (message) toast.success(message);
      dispatch(reset());
    }
    if (isEditError) {
      if (message) toast.error(message);
      dispatch(reset());
    }
  }, [isEditSuccess, isEditError, message, dispatch]);

  // Password validation
  const validatePassword = () => {
    if (!passwordData.newPassword) {
      toast.error("Lütfen yeni şifrenizi giriniz");
      return false;
    }
    if (passwordData.newPassword.length < 6 && passwordData.newPassword !== "-") {
      toast.error("Şifre en az 6 karakter olmalıdır");
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Şifreler eşleşmiyor");
      return false;
    }
    return true;
  };

  // Handle password input changes
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Step 1: Request OTP
  const handleRequestOTP = () => {
    if (!validatePassword()) return;
    
    dispatch(updateProfile({
      new_password: passwordData.newPassword
    }));
    // Don't immediately set step 2, wait for success response
    setOtpSent(true);
  };

  // Step 2: Verify OTP and update password
  const handleUpdatePassword = () => {
    if (!passwordData.otp) {
      toast.error("Lütfen doğrulama kodunu giriniz");
      return;
    }
    
    dispatch(updateProfile({
      new_password: passwordData.newPassword,
      otp: passwordData.otp
    }));
  };

  // Reset password form
  const resetPasswordForm = () => {
    setPasswordData({
      newPassword: "",
      confirmPassword: "",
      otp: ""
    });
    setPasswordChangeStep(1);
    setOtpSent(false);
  };

  // Handle API responses for password change
  useEffect(() => {
    if (isEditSuccess) {
      // If we're in step 1 and got success, it means OTP was sent successfully
      if (passwordChangeStep === 1 && otpSent) {
        // Move to step 2 for OTP verification
        setPasswordChangeStep(2);
        return;
      }
      
      // If we're in step 2 and got success, it means password was actually updated
      if (passwordChangeStep === 2 && otpSent) {
        resetPasswordForm();
      }
    }
  }, [isEditSuccess, otpSent, passwordChangeStep]);

  return (
    <div className="shadow-md rounded px-8 pt-6 pb-8 mb-4">
      <h2 className="text-2xl font-semibold text-white-100 mb-4 flex items-center gap-4">
        Profilim{" "}
      </h2>
      <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        <div className="bg-gray-900 p-4 space-y-4 rounded shadow">
          <h2 className="text-xl font-semibold text-white-100">
            Kişisel Bilgilerim
          </h2>
          <p className="text-white-100">
            <span className="font-semibold">ID:</span>
            <span className="ml-2">{profile.id}</span>
          </p>

          <p className="text-white-100">
            <span className="font-semibold">Bayi Adı:</span>
            <span className="ml-2">{profile.name}</span>
          </p>
          <p className="text-white-100">
            <span className="font-semibold">Telefon Numarası:</span>
            <span className="ml-2">{profile.phone}</span>
          </p>
        </div>
        <div className="bg-gray-900 p-4 space-y-4 rounded shadow">
          <h2 className="text-xl font-semibold text-white-100">Bayilik</h2>
          <p className="text-white-100">
            <span className="font-semibold">Durum:</span>
            <span className="ml-2">
              {profile.is_active === true ? (
                <OrderIndicator color={"green"}>Aktif</OrderIndicator>
              ) : (
                <OrderIndicator color={"red"}>Pasif</OrderIndicator>
              )}
            </span>
          </p>
          <p className="text-white-100">
            <span className="font-semibold">Alt Bayi Key:</span>

            <span className="ml-2">
              {profile?.payment_key || "Tanımlanmamış"}
            </span>
            <span className="text-xs block text-primary font-medium">Dekont Açıklamasına Yazılacak</span>
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mt-4">

        <div className="bg-gray-900 p-4 space-y-4 rounded shadow">
          <h2 className="text-xl font-semibold text-white-100">Finans</h2>
          <p className="text-white-100">
            <span className="font-semibold">Hesap Bakiyesi:</span>
            <span className="ml-2">{profile.pbalance_try}₺</span>
          </p>
          <p className="text-white-100">
            <span className="font-semibold">1 Elmas Fiyatı:</span>
            <span className="ml-2">{profile.calculator_diamond_price}₺</span>
          </p>
        </div>
        <div className="bg-gray-900 p-4 space-y-4 rounded shadow">
          <h2 className="text-xl font-semibold text-white-100">
            Şifre Değiştir
          </h2>
          
          {passwordChangeStep === 1 ? (
            // Step 1: Password Input
            <div className="space-y-4">
              <div>
                <label className="block text-white-100 text-sm font-medium mb-2">
                  Yeni Şifre
                </label>
                <div className="relative">
                  <input
                    className="w-full rounded-md pl-3 pr-10 py-2 focus:outline-none"
                    type={showPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                    placeholder="Yeni şifrenizi giriniz"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <AiOutlineEyeInvisible className="h-5 w-5 text-gray-400" />
                    ) : (
                      <AiOutlineEye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-white-100 text-sm font-medium mb-2">
                  Yeni Şifre Tekrar
                </label>
                <div className="relative">
                  <input
                    className="w-full rounded-md pl-3 pr-10 py-2 focus:outline-none"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
                    placeholder="Yeni şifrenizi tekrar giriniz"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <AiOutlineEyeInvisible className="h-5 w-5 text-gray-400" />
                    ) : (
                      <AiOutlineEye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              
              <button
                onClick={handleRequestOTP}
                disabled={isEditLoading}
                className="bg-secondary rounded-md px-4 py-2 text-sm text-white-100 font-medium w-full"
              >
                {isEditLoading ? (
                  <BiLoaderCircle className='animate-spin mx-auto' />
                ) : (
                  'Doğrulama Kodu Gönder'
                )}
              </button>
              
              <p className="text-xs text-white-100">
                En az 6 karakter olmalıdır.
              </p>
            </div>
          ) : (
            // Step 2: OTP Verification
            <div className="space-y-4">
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                <p className="text-sm">
                  Doğrulama kodu {profile.phone} numarasına gönderildi.
                </p>
              </div>
              
              <div>
                <label className="block text-white-100 text-sm font-medium mb-2">
                  Doğrulama Kodu
                </label>
                <input
                  className="w-full rounded-md pl-3 py-2 focus:outline-none"
                  type="text"
                  value={passwordData.otp}
                  onChange={(e) => handlePasswordChange("otp", e.target.value)}
                  placeholder="6 haneli doğrulama kodunu giriniz"
                  maxLength="6"
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleUpdatePassword}
                  disabled={isEditLoading}
                  className="bg-secondary rounded-md px-4 py-2 text-sm text-black-100 font-medium flex-1"
                >
                  {isEditLoading ? (
                    <BiLoaderCircle className='animate-spin mx-auto' />
                  ) : (
                    'Şifreyi Güncelle'
                  )}
                </button>
                
                <button
                  onClick={resetPasswordForm}
                  disabled={isEditLoading}
                  className="bg-gray-600 rounded-md px-4 py-2 text-sm text-black-100 font-medium"
                >
                  İptal
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileContainer;
